import { DataGridPro, GridSortModel } from '@mui/x-data-grid-pro';
import { GridCellParams } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { UserRole } from '../../../data/models/user-role.model';
import { UserGroup } from '../../../data/models/user-group.model';
import { IconButton, Paper, styled, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

const PREFIX = 'RoleDataTable';

export const classes = {
  container: `${PREFIX}-container`,
  grid: `${PREFIX}-grid`,
};

const DataTableContainer = styled(Paper)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    justifyContent: 'stretch',
    minWidth: 0,
  },

  [`& .${classes.grid}`]: {
    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus':
      {
        outline: 'none',
      },
    '& .MuiDataGrid-row:hover': {
      cursor: 'pointer',
    },
  },
}));

interface RoleDataTableProps {
  roles: UserRole[];
  groups: UserGroup[];
  loading: boolean;
  readOnly: boolean;
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
  onSortModelChange: (model: GridSortModel) => void;
  sortModel: GridSortModel;
}

export default function RoleDataTable({
  roles,
  groups,
  loading,
  readOnly,
  onEdit,
  onDelete,
  onSortModelChange,
  sortModel,
}: RoleDataTableProps) {
  const { t } = useTranslation('common');
  const isSuperReaderUser = useIsSuperReaderUser();

  const renderGroups = (params: GridCellParams) => {
    const grps = params.row.userGroups?.map((ugroups: any) => {
      const pid = ugroups.userGroupID;
      const grp = groups.find((p) => p.userGroupID === pid);
      return grp?.userGroupBC || '';
    });
    const s = grps?.join(', ');
    return <div title={s}>{s}</div>;
  };

  const columns = [
    {
      field: 'userRoleBC',
      headerName: t('roles.roleName'),
      flex: 1,
    },
    {
      field: 'description',
      headerName: t('roles.description'),
      flex: 1,
    },
    {
      field: 'groups',
      headerName: t('roles.groups'),
      flex: 2,
      renderCell: renderGroups,
    },
    {
      field: 'Actions',
      headerName: ' ',
      width: 120,
      hide: readOnly,
      renderCell: (params: GridCellParams) => {
        return (
          <>
            {!isSuperReaderUser && (
              <Tooltip title={t('roles.editRole')}>
                <IconButton
                  data-testid="edit"
                  onClick={() => onEdit(params.row.userRoleID)}
                  size="large">
                  <EditIcon color="primary" />
                </IconButton>
              </Tooltip>
            )}
            {!isSuperReaderUser && (
              <Tooltip title={t('roles.deleteRole')}>
                <IconButton
                  data-testid="edit"
                  onClick={() => onDelete(params.row.userRoleID)}
                  size="large">
                  <DeleteForeverIcon color="error" />
                </IconButton>
              </Tooltip>
            )}
          </>
        );
      },
      sortable: false,
    },
  ];

  return (
    <DataTableContainer data-testid="role-table" className={classes.container}>
      <DataGridPro
        getRowId={(row) => row.userRoleID!}
        rows={roles}
        columns={columns}
        rowCount={roles.length}
        autoHeight={true}
        loading={loading}
        className={classes.grid}
        checkboxSelection={false}
        disableRowSelectionOnClick={true}
        disableColumnMenu={true}
        disableColumnSelector={true}
        hideFooter={true}
        sortingMode="server"
        initialState={{
          sorting: {
            sortModel: [{ field: 'description', sort: 'asc' }],
          },
        }}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        sortingOrder={['asc', 'desc']}
      />
    </DataTableContainer>
  );
}
